import { useMemo, useState } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';
import { WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import { useQueryState } from 'hooks/useQueryState';

import { useQueryRiskDensityBy } from 'api/queries/riskDensityBy.query';

import { TDateRangeOption } from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter';
import { getInputLabel } from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter.utils';
import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import {
    SelectByOptionsValue,
    resolutionOptions,
    selectByOptions,
} from 'components/widgets/RiskDensity/RiskDensity.utils';

const DEFAULT_SELECT_BY = 'heatMapActivityStarts';

export const useRiskDensityProvider = ({
    widgetId,
    contractId,
    projectId,
    latestVersionId,
    globalFilterData,
    localFilters,
    widgetViewMode,
    componentKey,
    endpoints,
}) => {
    const { isGenerativeAi, reportId } = usePinboardWidgetWrapperContext();
    const isInPinboard = Boolean(widgetId);

    const [startProjectDate, setStartProjectDate] = useState<number | null>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.startProjectDate : null,
    );

    const [endProjectDate, setEndProjectDate] = useState<number | null>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.endProjectDate : null,
    );
    const [dateRange, setDateRange] = useState<{ startDate: number | null; endDate: number | null }>({
        startDate: WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.dateRange?.startDate : null,
        endDate: WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.dateRange?.endDate : null,
    });
    const [dateRangeOption, setDateRangeOption] = useState<TDateRangeOption>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.dateRangeOption : 'wholeProject',
    );
    const [resolution, setResolution] = useState<boolean>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters.resolution : false,
    );

    const [riskDensityBy, setRiskDensityBy] = useQueryState(
        'riskDensityBy',
        WidgetViewModeUtils.isPageView(widgetViewMode) ? DEFAULT_SELECT_BY : undefined,
    );
    const initialSelectBy = useMemo(() => {
        // backward compatability for OLD PINBOARD -> When pinboard is DEAD DELETE THIS IF CODE
        if (WidgetViewModeUtils.isPinboardView(widgetViewMode)) {
            return localFilters?.selectBy.value;
        }

        // in page view so get from URL
        if (WidgetViewModeUtils.isPageView(widgetViewMode) && riskDensityBy) {
            return riskDensityBy;
        }
        // in report view so get from widget params (localFilters)
        if (WidgetViewModeUtils.isReportView(widgetViewMode) && localFilters?.selectBy) {
            return localFilters?.selectBy;
        }
        // in add widget mode so set default
        if (WidgetViewModeUtils.isReportAddWidget(widgetViewMode)) {
            return DEFAULT_SELECT_BY;
        }
        // in edit widget mode but no riskDensityBy in local filters so set default
        if (WidgetViewModeUtils.isReportEditWidget(widgetViewMode) && localFilters?.selectBy) {
            return localFilters?.selectBy;
        }
        return null;
    }, [riskDensityBy, widgetViewMode, localFilters?.selectBy]);

    const [selectBy, setSelectBy] = useState<SelectByOptionsValue | null>(
        initialSelectBy ? selectByOptions.find((filter) => filter.value === initialSelectBy)?.value || null : null,
    );

    const source = isInPinboard
        ? localFilters
        : {
              ...globalFilterData,
              dateRange,
              resolution,
              startProjectDate,
              endProjectDate,
              dateRangeOption,
              riskDensityBy,
              selectBy,
          };

    const filters = useMemo(
        () =>
            getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.isExclude)
                    .map((i) => i.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.isExclude)
                    .map((i) => i.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
            }),

        [source?.activityCodes, source?.milestoneFragnet, source?.tags, source?.wbs],
    );

    const pillsData = {
        wbs: source?.wbs,
        milestoneFragnet: source?.milestoneFragnet,
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
        resolution: resolutionOptions.find((item) => item.value === source?.resolution)?.label,
        dateRange: getInputLabel(source?.dateRange),
    };

    const { pillsList: pillsFromHook } = useGlobalFiltersHook({ source: pillsData, latestVersionId, widgetId });
    const pills = useMemo(() => pillsFromHook, [JSON.stringify(pillsFromHook)]);

    const payload = useMemo(() => {
        return {
            id: latestVersionId ? [latestVersionId] : [],
            filter: {
                filters,
            },
            startDate: source?.dateRange?.startDate,
            endDate: source?.dateRange?.endDate,
            isWeekResolution: source?.resolution,
            selectBy: source?.selectBy,
            ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId, reportId),
        };
    }, [
        latestVersionId,
        filters,
        source?.dateRange?.startDate,
        source?.dateRange?.endDate,
        source?.resolution,
        source?.selectBy,
        isGenerativeAi,
        componentKey,
        widgetId,
    ]);

    const state = {
        project: {
            projectId,
            contractId,
            latestVersionId,
        },
        globalFilterData: {
            activityCodes: source?.activityCodes,
            milestoneFragnet: source?.milestoneFragnet,
            tags: source?.tags,
            wbs: source?.wbs,
        },
        local: {
            dateRange: source?.dateRange,
            resolution: source?.resolution,
            startProjectDate: source?.startProjectDate,
            endProjectDate: source?.endProjectDate,
            dateRangeOption: source?.dateRangeOption,
            riskDensityBy: source?.riskDensityBy,
            selectBy: source?.selectBy,
        },
    };

    return {
        ui: {
            state: {
                ...state.project,
                ...state.globalFilterData,
                ...state.local,
            },
            isInPinboard: Boolean(widgetId),
            pills,
        },
        api: {
            riskDensityBy: {
                instance: useQueryRiskDensityBy(payload, selectBy && endpoints[selectBy]),
                payload: payload,
            },
        },
        setStartProjectDate,
        setEndProjectDate,
        setDateRange,
        setDateRangeOption,
        setRiskDensityBy,
        setResolution,
        setSelectBy,
    };
};
